/* eslint-disable react/prop-types */
"use client";
import React, { useEffect, useState } from "react";
import Slider from "@mui/material/Slider";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../../styles/CalendarOverride.css";
import api from "../../api/axios";
import { useTranslation } from "react-i18next";
import EventFilterSheet from "./EventFilterSheet";
import { getEventDiscoveryLocationParams } from "../../utils/eventDiscoveryRadius";

const CATEGORY_VALUES = ["party", "sport", "recreation", "education", "traveling"];
const LANGUAGE_VALUES = ["en", "sk", "cs", "ru", "de", "uk", "pl", "hu", "fr", "es", "it"];
const EMPTY_FILTERS = { eventKind: "", audienceGender: "", eventLanguage: "", participantAgeMin: "", participantAgeMax: "", socialFit: "" };

function ChoiceGroup({ title, options, value, onChange, multiple = false, first = false }) {
  return (
    <section className={first ? "mt-2" : "mt-7"}>
      <h3 className="mb-3 text-[19px] font-semibold text-white">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {options.map(({ value: optionValue, label }) => {
          const active = multiple ? value.includes(optionValue) : value === optionValue;
          return (
            <button
              key={optionValue}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(multiple
                ? active ? value.filter((item) => item !== optionValue) : [...value, optionValue]
                : active ? "" : optionValue)}
              className={`min-h-10 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${active
                ? "border-white bg-white text-[#3E6FFB]"
                : "border-white/25 bg-white/10 text-white hover:bg-white/20"}`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function EventDateFilter({
  expandedHeader = false,
  onClose, selectedDate, selectedCity, selectedMunicipalityId, selectedCountry, selectedRadiusKm,
  selectedCategories = [], selectedDiscoveryFilters = EMPTY_FILTERS,
  fixedEventKind = "",
  includeTeamConcepts = false,
  onCategoriesChange, onDiscoveryFiltersChange, viewType = "all", isGuest = false,
  onDateChange, onApply, dateCount, setDateCount,
}) {
  const { t } = useTranslation("events");
  const label = (key, fallback) => t(`discovery_filters.${key}`, { defaultValue: fallback });
  const showDateFilter = fixedEventKind !== "concept";
  const [draftDate, setDraftDate] = useState(showDateFilter ? selectedDate : null);
  const [draftCategories, setDraftCategories] = useState(selectedCategories);
  const [draftFilters, setDraftFilters] = useState({ ...EMPTY_FILTERS, ...selectedDiscoveryFilters });
  const [count, setCount] = useState(showDateFilter ? dateCount : null);
  const [countError, setCountError] = useState(false);
  const [socialFitAvailable, setSocialFitAvailable] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [showFitHelp, setShowFitHelp] = useState(false);
  const ageRange = [Number(draftFilters.participantAgeMin) || 18, Number(draftFilters.participantAgeMax) || 100];

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setCount(null);
      setCountError(false);
      api.get("/events/events_packs/", {
        params: {
          count_only: 1,
          type: viewType,
          city: selectedCity,
          ...(selectedCity && selectedMunicipalityId ? { municipality_id: selectedMunicipalityId } : {}),
          country: selectedCountry,
          ...(selectedCity ? getEventDiscoveryLocationParams(selectedRadiusKm) : {}),
          ...(showDateFilter && draftDate ? { date: draftDate } : {}),
          ...(draftCategories.length ? { category: draftCategories } : {}),
          ...((fixedEventKind || draftFilters.eventKind) ? { event_kind: fixedEventKind || draftFilters.eventKind } : {}),
          ...(includeTeamConcepts ? { include_team_concepts: 1 } : {}),
          ...(draftFilters.audienceGender ? { audience_gender: draftFilters.audienceGender } : {}),
          ...(draftFilters.eventLanguage ? { event_language: draftFilters.eventLanguage } : {}),
          ...(draftFilters.participantAgeMin ? { participant_age_min: draftFilters.participantAgeMin, participant_age_max: draftFilters.participantAgeMax } : {}),
          ...(draftFilters.socialFit && ["all", "for_you"].includes(viewType) ? { social_fit: draftFilters.socialFit } : {}),
        },
        signal: controller.signal,
        authOptional: true,
        suppressUnauthorizedRedirect: true,
      }).then(({ data }) => {
        if (typeof data?.count === "number") setCount(data.count);
        else setCountError(true);
        if (typeof data?.social_fit_available === "boolean") {
          setSocialFitAvailable(data.social_fit_available);
          if (!data.social_fit_available) {
            setDraftFilters((current) => current.socialFit ? { ...current, socialFit: "" } : current);
          }
        }
      }).catch((error) => {
        if (error?.name !== "CanceledError" && error?.name !== "AbortError") setCountError(true);
      });
    }, 180);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [draftDate, draftCategories, draftFilters, fixedEventKind, includeTeamConcepts, showDateFilter, selectedCity, selectedMunicipalityId, selectedCountry, selectedRadiusKm, viewType, retryKey]);

  const updateFilter = (key, value) => setDraftFilters((current) => ({ ...current, [key]: value }));
  const clear = () => {
    setDraftDate(null);
    setDraftCategories([]);
    setDraftFilters({ ...EMPTY_FILTERS });
  };
  const hasSelection = Boolean((showDateFilter && draftDate) || draftCategories.length || Object.values(draftFilters).some(Boolean));
  const apply = (closeSheet) => {
    if (showDateFilter) onDateChange(draftDate);
    onCategoriesChange(draftCategories);
    onDiscoveryFiltersChange(draftFilters);
    setDateCount(count);
    closeSheet(onApply);
  };

  return (
    <EventFilterSheet
      expandedHeader={expandedHeader}
      onClose={onClose}
      closeLabel={showDateFilter ? t("date_filter.header.close_aria") : t("filters.sheet.close")}
      contentClassName="vg-event-date-filter-scroll"
      footer={({ closeSheet }) => (
        <div className="flex w-full gap-2">
          <button
            type="button"
            className={`h-[55px] min-w-0 flex-1 rounded-full border border-white/20 text-[15px] font-semibold ${count == null && !countError ? "bg-white/30 text-white/50" : "bg-white text-[#3E6FFB]"}`}
            disabled={count == null && !countError}
            onClick={() => countError ? setRetryKey((key) => key + 1) : apply(closeSheet)}
          >
            {countError ? label("count_error", "Try again") : count == null
              ? t(showDateFilter ? "date_filter.body.show_events" : "date_filter.body.show_concepts")
              : t(showDateFilter ? "date_filter.body.show_events_with_count" : "date_filter.body.show_concepts_with_count", { count })}
          </button>
          {hasSelection && (
            <button type="button" className="h-[55px] shrink-0 rounded-full border border-white/20 bg-white/10 px-4 text-sm font-semibold text-white" onClick={clear}>
              {t("date_filter.body.clear_filters", { defaultValue: "Clear filters" })}
            </button>
          )}
        </div>
      )}
    >
      {showDateFilter ? <section>
        <h3 className="vg-event-date-filter-title mb-[15px] text-[24px] font-semibold leading-[22px] tracking-[-0.4px] text-white">
          {t("date_filter.body.pick_date")}
        </h3>
        <div className="vg-event-date-filter-calendar-shell overflow-hidden rounded-[28px] bg-white p-2 text-center text-black shadow-[0_18px_44px_rgba(0,0,0,0.18)]">
          <Calendar
            onChange={(value) => {
              const dateObj = new Date(value);
              const yyyy = dateObj.getFullYear();
              const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
              const dd = String(dateObj.getDate()).padStart(2, "0");
              setDraftDate(`${yyyy}-${mm}-${dd}`);
            }}
            value={draftDate ? new Date(draftDate) : null}
            minDetail="year" maxDetail="month" next2Label={null} prev2Label={null}
            className="vg-event-filter-calendar rounded-2xl w-full"
          />
        </div>
      </section> : null}
      {!fixedEventKind ? (
        <ChoiceGroup
          title={label("kind", "Type")}
          value={draftFilters.eventKind}
          onChange={(value) => updateFilter("eventKind", value)}
          options={[{ value: "event", label: label("event", "Event") }, { value: "concept", label: label("concept", "Concept") }]}
        />
      ) : null}
      <ChoiceGroup
        title={label("categories", "Categories")}
        first={!showDateFilter && Boolean(fixedEventKind)}
        multiple value={draftCategories} onChange={setDraftCategories}
        options={CATEGORY_VALUES.map((value) => ({
          value,
          label: t(`event_create.category_grid.${({ recreation: "adventures", education: "educational", traveling: "games" })[value] || value}`),
        }))}
      />
      <ChoiceGroup
        title={label("audience", "Audience")}
        value={draftFilters.audienceGender}
        onChange={(value) => updateFilter("audienceGender", value)}
        options={[
          { value: "female", label: label("female", "Women") },
          { value: "male", label: label("male", "Men") },
          { value: "mixed", label: label("mixed", "Mixed") },
        ]}
      />
      <section className="mt-7">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-[19px] font-semibold text-white">{label("age", "Participant age")}</h3>
          <span className="text-sm tabular-nums text-white/75">{draftFilters.participantAgeMin ? `${ageRange[0]}–${ageRange[1]}` : label("any_age", "Any age")}</span>
        </div>
        <div className="px-2">
          <Slider
            value={ageRange}
            min={18} max={100} step={1}
            disableSwap
            aria-label={label("age", "Participant age")}
            getAriaLabel={(index) => label(index === 0 ? "age_min" : "age_max", index === 0 ? "Minimum age" : "Maximum age")}
            onChange={(_, value) => {
              if (!Array.isArray(value)) return;
              setDraftFilters((current) => ({ ...current,
                participantAgeMin: value[0] === 18 && value[1] === 100 ? "" : String(value[0]),
                participantAgeMax: value[0] === 18 && value[1] === 100 ? "" : String(value[1]),
              }));
            }}
            sx={{ height: 6, color: "#3E6FFB", "& .MuiSlider-thumb": { width: 20, height: 20, backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,.25)" }, "& .MuiSlider-rail": { backgroundColor: "#fff", opacity: .3 }, "& .MuiSlider-track": { border: "none" } }}
          />
        </div>
      </section>
      <section className="mt-7">
        <label htmlFor="event-language" className="mb-3 block text-[19px] font-semibold text-white">{label("language", "Language")}</label>
        <select id="event-language" value={draftFilters.eventLanguage} onChange={(event) => updateFilter("eventLanguage", event.target.value)} className="h-11 min-w-44 rounded-full border border-white/25 bg-white/10 px-4 text-sm text-white">
          <option value="">{label("any_language", "Any language")}</option>
          {LANGUAGE_VALUES.map((value) => <option key={value} value={value}>{label(`language_${value}`, value.toUpperCase())}</option>)}
        </select>
      </section>
      {!isGuest && ["all", "for_you"].includes(viewType) && socialFitAvailable === true && (
        <section className="mt-7">
          <div className="mb-3 flex items-center gap-2">
            <h3 className="text-[19px] font-semibold text-white">{label("social_fit", "Fit with your social preferences")}</h3>
            <button type="button" aria-label={label("social_fit_help_aria", "Explain social fit")} aria-expanded={showFitHelp} onClick={() => setShowFitHelp((value) => !value)} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/50 text-xs font-semibold text-white">?</button>
          </div>
          {showFitHelp && <p className="mb-3 text-sm leading-relaxed text-white/80">{label("social_fit_help", "Shows events that match the social preferences you chose. Excellent fit is closest; broader fit includes more events.")}</p>}
          <div className="flex flex-wrap gap-2">
            {[["strong", "strong"], ["good", "good"], ["low", "low"]].map(([value, key]) => <button key={value} type="button" aria-pressed={draftFilters.socialFit === value} onClick={() => updateFilter("socialFit", draftFilters.socialFit === value ? "" : value)} className={`min-h-10 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${draftFilters.socialFit === value ? "border-white bg-white text-[#3E6FFB]" : "border-white/25 bg-white/10 text-white hover:bg-white/20"}`}>{label(key, value)}</button>)}
          </div>
        </section>
      )}
      <div className="h-4" />
    </EventFilterSheet>
  );
}

export default EventDateFilter;
